import { useMemo } from "react";
import { useAuth } from "./AuthContext.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { orgRolePermissions } from "../orgs/permissions.js";
import type { Permission } from "../types.js";

/**
 * Combined UI authorization check, mirroring the API's authorize + authorizeOrg
 * split. Returns true when the user holds the permission GLOBALLY (platform
 * admin / superadmin) OR when the currently selected org's role grants it
 * (org-scoped permission).
 *
 * Use this for org-scoped controls (product:*, passport:*, access:manage,
 * supplier:manage, integration:manage, import:run). For PLATFORM permissions
 * (user:*, role:*, audit:*, system:*, org:manage) use useAuth().hasPermission
 * directly — those never come from an org role.
 */
export function useCan(): (p: Permission) => boolean {
  const { hasPermission } = useAuth();
  const { org } = useOrgs();
  const orgPerms = useMemo(() => orgRolePermissions(org?.memberRole), [org?.memberRole]);
  return (p: Permission) => hasPermission(p) || orgPerms.has(p);
}
